import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { api, ApiError, files as fileApi } from '../lib/api';

/**
 * Adjuntos de un documento. En modo edición (dentro del detalle) permite subir
 * y borrar; en modo lectura (vista compartida con el cliente) solo listar y
 * descargar. Pensado para archivar los PDF originales del histórico.
 */

interface Attachment { id: string; filename: string; mimeType: string; sizeBytes: number }

const humanSize = (b: number): string =>
  b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

const iconFor = (mime: string): string =>
  mime.includes('pdf') ? 'file' : mime.startsWith('image/') ? 'file' : 'file';

export function Attachments({
  docId, shareToken, readOnly,
}: {
  docId?: string;
  shareToken?: string;
  readOnly?: boolean;
}) {
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const basePath = shareToken ? `/public/doc/${shareToken}/attachments` : `/documents/${docId}/attachments`;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get<{ attachments: Attachment[] }>(basePath);
      setItems(r.attachments);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [docId, shareToken]);

  // Sube archivos convirtiéndolos a base64.
  const onFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || !docId) return;
    setBusy(true); setErr(null);
    try {
      const encoded = await Promise.all(Array.from(fileList).map((f) => new Promise<{ filename: string; mimeType: string; data: string }>((resolve, reject) => {
        if (f.size > 25 * 1024 * 1024) { reject(new Error(`${f.name} supera 25 MB.`)); return; }
        const r = new FileReader();
        r.onload = () => resolve({ filename: f.name, mimeType: f.type || 'application/octet-stream', data: String(r.result) });
        r.onerror = () => reject(new Error(`No se pudo leer ${f.name}.`));
        r.readAsDataURL(f);
      })));
      await api.post(`${basePath}`, { files: encoded });
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'No se pudieron subir los archivos.');
    } finally { setBusy(false); }
  };

  const download = async (a: Attachment) => {
    try {
      const url = await fileApi.objectUrl(`${basePath}/${a.id}`);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setErr('No se pudo abrir el archivo.'); }
  };

  const remove = async (a: Attachment) => {
    if (!docId) return;
    setBusy(true);
    try { await api.del(`${basePath}/${a.id}`); await load(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'No se pudo eliminar.'); }
    finally { setBusy(false); }
  };

  // En la vista del cliente, si no hay adjuntos no se muestra nada.
  if (readOnly && !loading && items.length === 0) return null;

  return (
    <div className="attach">
      <div className="attach__head">
        <span className="eyebrow">Documentos adjuntos</span>
        {!readOnly && docId && (
          <label className={`btn btn--ghost btn--sm${busy ? ' is-busy' : ''}`}>
            <Icon name="plus" size={14} /> Adjuntar archivos
            <input type="file" multiple style={{ display: 'none' }} disabled={busy}
              onChange={(e) => { void onFiles(e.target.files); e.currentTarget.value = ''; }} />
          </label>
        )}
      </div>

      {loading ? (
        <div className="faint attach__empty">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="faint attach__empty">
          {readOnly ? '' : 'Sin adjuntos. Sube aquí el PDF original de este trabajo (cotización o contrato previo).'}
        </div>
      ) : (
        <ul className="attach__list">
          {items.map((a) => (
            <li key={a.id} className="attach__item">
              <button className="attach__main" onClick={() => download(a)} title="Abrir">
                <Icon name={iconFor(a.mimeType) as 'file'} size={17} />
                <span className="attach__name">{a.filename}</span>
                <span className="attach__size">{humanSize(a.sizeBytes)}</span>
              </button>
              {!readOnly && docId && (
                <button className="attach__del" onClick={() => remove(a)} disabled={busy} title="Eliminar">
                  <Icon name="trash" size={15} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {err && <div className="act-error" style={{ marginTop: 8 }}><Icon name="alert" size={15} />{err}</div>}
    </div>
  );
}
